import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Avatar, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useBranding } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/config';
import {
  Home,
  School,
  SportsEsports,
  Chat,
  Campaign,
  Policy,
  Settings,
  Analytics,
  People,
  CalendarMonth,
  FolderOpen,
  EmojiEvents,
  Store,
  AutoStories,
  Forum,
  Stars,
  Assignment,
  LiveTv,
  AdminPanelSettings,
  Spa,
  Diversity3,
  Logout,
} from '@mui/icons-material';

const RAIL_WIDTH = 80;

const RAIL_STYLES = {
  navSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    paddingBottom: '8px',
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.2)', borderRadius: '2px' },
    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.3)' },
  },
  navItemsGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
  },
};

interface NotificationCounts {
  announcements: number;
  learning: number;
  games: number;
  store: number;
  chat: number;
  tasks: number;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
  moduleName?: string;
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <Home />, path: '/dashboard' },
  { id: 'announcements', label: 'Announcements', icon: <Campaign />, path: '/announcements', moduleName: 'announcements' },
  { id: 'policies', label: 'Policies', icon: <Policy />, path: '/policies', moduleName: 'policies' },
  { id: 'culture', label: 'Culture Hub', icon: <Diversity3 />, path: '/culture', moduleName: 'cultureHub' },
  { id: 'learning', label: 'Learning', icon: <School />, path: '/learning', moduleName: 'learning' },
  { id: 'microlearning', label: 'Microlearning', icon: <AutoStories />, path: '/microlearning', moduleName: 'microlearning' },
  { id: 'games', label: 'Games', icon: <SportsEsports />, path: '/gamification', moduleName: 'games' },
  { id: 'wellness', label: 'Wellness', icon: <Spa />, path: '/wellness', moduleName: 'wellness' },
  { id: 'rewards', label: 'Rewards', icon: <EmojiEvents />, path: '/rewards', moduleName: 'rewards' },
  { id: 'recognition', label: 'Recognition', icon: <EmojiEvents />, path: '/recognition', moduleName: 'recognition' },
  { id: 'store', label: 'Store', icon: <Store />, path: '/store', moduleName: 'rewards' },
  { id: 'pulse', label: 'Pulse', icon: <Forum />, path: '/pulse', moduleName: 'chat' },
  { id: 'tasks', label: 'Tasks', icon: <Assignment />, path: '/projects', moduleName: 'projects' },
  { id: 'calendar', label: 'Calendar', icon: <CalendarMonth />, path: '/calendar', moduleName: 'calendar' },
  { id: 'documents', label: 'Documents', icon: <FolderOpen />, path: '/documents', moduleName: 'policies' },
  { id: 'chat', label: 'Chat', icon: <Chat />, path: '/chat', moduleName: 'chat' },
  { id: 'streaming', label: 'Live Stream', icon: <LiveTv />, path: '/streaming', moduleName: 'livestream' },
];

// Single Admin Hub link instead of extended menu
const adminItems: NavItem[] = [
  { id: 'admin-hub', label: 'Admin Hub', icon: <AdminPanelSettings />, path: '/admin', adminOnly: true },
];

function NavigationRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [cultureScheduledToday, setCultureScheduledToday] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState<NotificationCounts>({
    announcements: 0,
    learning: 0,
    games: 0,
    store: 0,
    chat: 0,
    tasks: 0,
  });

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await apiClient.get('/api/company/modules');
        if (response.data?.success && response.data?.data) {
          const activeModules = response.data.data
            .filter((m: { isActive: boolean }) => m.isActive)
            .map((m: { moduleName: string }) => m.moduleName);
          setEnabledModules(activeModules);
        }
      } catch (error) {
        setEnabledModules(['wellness', 'learning', 'microlearning', 'games', 'announcements', 'chat', 'projects', 'rewards', 'recognition', 'policies', 'analytics', 'livestream', 'calendar', 'documents', 'cultureHub', 'pulse', 'badges', 'askAria']);
      }
    };
    fetchModules();
    apiClient.get('/culture/is-scheduled-today').then(res => {
      if (res.data?.success && res.data?.data?.isScheduled) setCultureScheduledToday(true);
    }).catch(() => {});
  }, []);

  // Fetch notification counts for badges
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      try {
        const response = await apiClient.get('/notifications/counts');
        if (response.data?.success && response.data?.data) {
          setNotificationCounts(response.data.data);
        }
      } catch (error) {
        // Silently fail - badges will show 0
      }
    };

    fetchNotificationCounts();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchNotificationCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const isAdmin = user && ['admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';

  // Check if a module is enabled
  const isModuleEnabled = (moduleName?: string) => {
    if (!moduleName) return true;
    return enabledModules.includes(moduleName);
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get badge count for a nav item
  const getBadgeCount = (itemId: string): number => {
    switch (itemId) {
      case 'announcements':
        return notificationCounts.announcements;
      case 'policies':
        return (notificationCounts as any).policies || 0;
      case 'learning':
      case 'microlearning':
        return notificationCounts.learning;
      case 'games':
        return notificationCounts.games;
      case 'store':
        return notificationCounts.store;
      case 'chat':
      case 'pulse':
        return notificationCounts.chat;
      case 'tasks':
        return notificationCounts.tasks;
      default:
        return 0;
    }
  };

  const sidebarBg = branding.sidebarColor || '#1A1D1F';
  const sidebarText = branding.sidebarTextColor || '#FFFFFF';
  const primaryColor = branding.primaryColor || '#C0EB38';

  const resolveUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/r2/')) {
      const baseUrl = apiClient.defaults.baseURL || '';
      return `${baseUrl}${url}`;
    }
    return url;
  };

  return (
    <Box sx={{
      width: RAIL_WIDTH,
      height: '100vh',
      background: sidebarBg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '16px 0',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1200,
    }}>
      <Tooltip title={branding.companyName || 'MoreMe AI'} placement="right">
        <Box
          onClick={() => navigate(isSuperSuperAdmin ? '/admin/super' : '/dashboard')}
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: branding.logoUrl ? 'transparent' : primaryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          {branding.logoUrl ? (
            <Box
              component="img"
              src={resolveUrl(branding.logoUrl)}
              alt={branding.companyName || 'Logo'}
              sx={{ width: 40, height: 40, objectFit: 'contain' }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/brand/logo-mark.svg`}
              alt="MoreMe AI"
              sx={{ width: 28, height: 28, filter: 'brightness(10)' }}
            />
          )}
        </Box>
      </Tooltip>

      <Box sx={RAIL_STYLES.navSection}>
        {!isSuperSuperAdmin && (
        <Box sx={RAIL_STYLES.navItemsGroup}>
            {navItems.filter((item) => isModuleEnabled(item.moduleName)).map((item) => {
              const badgeCount = getBadgeCount(item.id);
              const active = isActive(item.path);
              return (
                <Tooltip 
                  key={item.id} 
                  title={item.label} 
                  placement="right"
                >
                  <span>
                    <IconButton
                      onClick={() => navigate(item.path)}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        color: active ? primaryColor : `${sidebarText}99`,
                        backgroundColor: active ? `${primaryColor}20` : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: active ? `${primaryColor}30` : `${sidebarText}15`,
                          color: active ? primaryColor : sidebarText,
                        },
                        '& svg': { fontSize: 24 },
                      }}
                    >
                    {(badgeCount > 0) || (item.id === 'culture' && cultureScheduledToday) ? (
                      <Badge
                        badgeContent={item.id === 'culture' && cultureScheduledToday && badgeCount === 0 ? '!' : (badgeCount > 99 ? '99+' : badgeCount)}
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: item.id === 'culture' && cultureScheduledToday ? '#8B5CF6' : '#EF4444',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 700,
                            minWidth: '18px',
                            height: '18px',
                            borderRadius: '9px',
                            padding: '0 4px',
                            border: `2px solid ${sidebarBg}`,
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            );
          })}
        </Box>
        )}

        {isAdmin && (
          <Box sx={{ ...RAIL_STYLES.navItemsGroup, pt: 2, borderTop: `1px solid ${sidebarText}1A`, mt: 2 }}>
            {adminItems.map((item) => (
              <Tooltip key={item.id} title={isSuperSuperAdmin ? 'Super Admin Dashboard' : item.label} placement="right">
                <IconButton
                  onClick={() => navigate(isSuperSuperAdmin ? '/admin/super' : item.path)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    color: isActive(item.path) ? primaryColor : `${sidebarText}99`,
                    backgroundColor: isActive(item.path) ? `${primaryColor}20` : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isActive(item.path) ? `${primaryColor}30` : `${sidebarText}15`,
                      color: isActive(item.path) ? primaryColor : sidebarText,
                    },
                    '& svg': { fontSize: 24 },
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        paddingTop: '16px',
        borderTop: `1px solid ${sidebarText}1A`,
        marginTop: 'auto',
      }}>
        {isSuperSuperAdmin ? (
          <Tooltip title="Logout" placement="right">
            <IconButton
              onClick={logout}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                color: `${sidebarText}99`,
                '&:hover': { backgroundColor: `${sidebarText}15`, color: '#EF4444' },
                '& svg': { fontSize: 24 },
              }}
            >
              <Logout />
            </IconButton>
          </Tooltip>
        ) : (
        <Tooltip title={`${user?.firstName} ${user?.lastName}`} placement="right">
          <Avatar
            src={user?.avatar}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              border: `2px solid ${sidebarText}40`,
              background: primaryColor,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: sidebarBg,
            }}
            onClick={() => navigate('/profile')}
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
        </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default NavigationRail;
